<script setup lang="ts">
// components
import AppTag from '~/components/redesign/AppTag.vue';
import Buttons from '~/components/redesign/Buttons';

// composables
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';
import { useDayjs } from '#dayjs';

// utils
import isString from '~/utils/isString';
import isObject from '~/utils/isObject';
import { getArticleLink } from '~/utils/getArticleLink';

// types
import type { AppTagProps } from '~/components/redesign/AppTag.vue';
import type { BlogArticle } from '~/types/blog';

export type CardsBlogProps = Partial<
  {
    image: {
      src: string | null;
      alt: string;
    };
    tag: {
      slug?: string;
      name: string;
    };
    part?: string;
    title: string;
    author: string;
    publishedAt: string;
    timeToRead: string;
  } & Pick<BlogArticle, 'type' | 'status' | 'slug' | 'categories'>
>;

const props = withDefaults(
  defineProps<CardsBlogProps & { orientation?: 'horizontal' | 'vertical' }>(),
  {
    image: () => ({}),
    orientation: 'horizontal',
  },
);

const dayjs = useDayjs();
const localePathWithSlash = useLocalePathWithSlash();

const sizesByOrientation = computed(() =>
  props.orientation === 'horizontal'
    ? 'xs:0px md:176px xl:176px'
    : 'xs:328px md:340px xl:384px',
);

const TAG_VARIANTS_BY_SLUG: Record<string, AppTagProps['variant']> = {
  technical: 'secondary',
  business: 'primary',
};

const formattedPublishedAt = dayjs(props.publishedAt).format('MMM Do YYYY');
</script>

<template>
  <NuxtLink
    :class="orientation"
    :to="localePathWithSlash(getArticleLink(props))"
    target="_blank"
    data-component-name="Cards.Blog"
  >
    <NuxtImg
      v-if="isString(image.src)"
      :src="image.src"
      :alt="image.alt"
      :sizes="sizesByOrientation"
      width="384"
      height="216"
      densities="x1 x2"
      format="webp"
    />

    <div class="content">
      <AppTag v-if="isObject(tag)" :variant="TAG_VARIANTS_BY_SLUG[tag.slug]">
        {{ tag.name }}
      </AppTag>

      <div class="container">
        <span v-if="isString(part)" class="part">
          {{ part }}
        </span>

        <h5 class="title">{{ title }}</h5>

        <strong class="author">{{ author }}</strong>

        <div class="bottom">
          <div class="meta">
            <time :datetime="publishedAt" :title="formattedPublishedAt" pubdate>
              {{ formattedPublishedAt }}
            </time>

            <template v-if="isString(timeToRead)">
              <span class="divider"> &middot; </span>

              <span class="time-to-read">
                {{ timeToRead }} {{ $t('min_to_read') }}
              </span>
            </template>
          </div>

          <Buttons.Card>
            {{ $t('Read_the_Blog') }}
          </Buttons.Card>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/mixins/text-transform';
@use '_/fn';
@use '_/variables/shadow';

[data-component-name='Cards.Blog'] {
  @include size.full;

  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: shadow.$elevation-3;
  }

  img {
    height: auto;
  }

  .content {
    [data-component-name='AppTag'] {
      pointer-events: none;
    }

    .container {
      span.part {
        @include typo.body-2;
      }

      h5.title {
        @include typo.subtitle-2;
        @include text-transform.truncate-to-rows(2);
      }

      strong.author {
        @include typo.subtitle-4;
      }

      .bottom {
        @include flex.center-sb;

        flex-wrap: wrap;

        .meta {
          @include typo.body-4;

          color: fn.token('text-secondary');

          @include flex.center-start;

          gap: 0 0.5rem;
        }

        [data-component-name='Buttons.Card'] {
          margin-top: auto;
          margin-left: auto;
        }
      }
    }
  }

  &.horizontal {
    @include flex.center-start;

    gap: 0 2rem;

    @include media.tablet {
      gap: 0 1.5rem;
    }

    img {
      max-width: 11rem;

      @include media.mobile {
        display: none;
      }
    }

    .content {
      flex-grow: 1;

      [data-component-name='AppTag'] {
        margin-bottom: 0.5rem;

        @include media.mobile {
          margin-bottom: 0.25rem;
        }
      }

      &:has([data-component-name='AppTag']) {
        padding: 2rem 0;

        @include media.tablet {
          padding: 1.5rem 0;
        }

        @include media.mobile {
          padding: 1rem 0;
        }
      }

      .container {
        h5.title {
          margin-bottom: 0.5rem;

          @include media.mobile {
            margin-bottom: 0.25rem;
          }
        }

        strong.author {
          display: none;
        }

        .bottom {
          gap: 0 1.5rem;

          @include media.mobile {
            gap: 0.5rem 1.5rem;
          }
        }
      }
    }

    &:has(img) {
      .content {
        [data-component-name='AppTag'] {
          display: none;
        }
      }
    }
  }

  &.vertical {
    @include flex.stretch-start;

    flex-direction: column;
    border: 1px solid fn.token('outline-secondary');

    img {
      width: 100%;
    }

    .content {
      @include flex.start-start;

      flex-direction: column;
      flex-grow: 1;
      padding: 1rem 2rem 2rem;

      @include media.tablet {
        padding: 1rem 1.5rem 1.5rem;
      }

      @include media.mobile {
        padding: 0.5rem 1rem 1rem;
      }

      .container {
        @include size.full;
        @include flex.start-start;

        flex-direction: column;
        flex-grow: 1;

        span.part {
          display: none;
        }

        h5.title {
          margin: 0.5rem 0;
        }

        .bottom {
          width: 100%;

          @include flex.start-sb;

          gap: 1rem;
          flex-grow: 1;
        }
      }
    }
  }
}
</style>
